
import React, { useState, useMemo, useEffect, useRef } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LineChart, Line, Legend, Cell } from 'recharts';
import type { WaterReading, Unit, Role, TariffWater, TariffCollection, ActivityLog } from '../../types';
import { UnitType } from '../../types';
import { useNotification } from '../../App';
import Modal from '../ui/Modal';
import StatCard from '../ui/StatCard';
import { 
    HomeIcon, StoreIcon, TrendingUpIcon, DropletsIcon, ChevronLeftIcon, ChevronRightIcon, 
    SearchIcon, UploadIcon, SparklesIcon, EyeIcon, 
    DocumentArrowDownIcon, WarningIcon,
    SaveIcon, LockClosedIcon, XMarkIcon, ArrowPathIcon, TrashIcon, ClockIcon, UserCircleIcon
} from '../ui/Icons';
import { parseUnitCode, getPreviousPeriod, sortUnitsComparator, formatCurrency, timeAgo } from '../../utils/helpers';
import { processImportFile } from '../../utils/importHelpers';
import { setLockStatus, saveWaterReadings, fetchRecentWaterReadings } from '../../services';
import Spinner from '../ui/Spinner';

declare const XLSX: any;

// --- Child Components ---
const MonthPickerPopover: React.FC<{
    currentPeriod: string;
    onSelectPeriod: (period: string) => void;
    onClose: () => void;
}> = ({ currentPeriod, onSelectPeriod, onClose }) => {
    const pickerRef = useRef<HTMLDivElement>(null);
    const [displayYear, setDisplayYear] = useState(new Date(currentPeriod + '-02').getFullYear());

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (pickerRef.current && !pickerRef.current.contains(event.target as Node)) {
                onClose();
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, [onClose]);

    const now = new Date();
    const currentSystemYear = now.getFullYear();

    const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

    return (
        <div ref={pickerRef} className="absolute top-full mt-2 left-0 z-20 bg-white p-4 rounded-xl shadow-lg border w-72">
            <div className="flex justify-between items-center mb-4">
                <button onClick={() => setDisplayYear(y => y - 1)} className="p-1 rounded-full hover:bg-gray-200"><ChevronLeftIcon /></button>
                <span className="font-bold text-lg">{displayYear}</span>
                <button onClick={() => setDisplayYear(y => y + 1)} disabled={displayYear >= currentSystemYear + 2} className="p-1 rounded-full hover:bg-gray-200 disabled:opacity-50"><ChevronRightIcon /></button>
            </div>
            <div className="grid grid-cols-3 gap-2">
                {months.map((month, index) => {
                    const monthNum = String(index + 1).padStart(2, '0');
                    const value = `${displayYear}-${monthNum}`;
                    const isSelected = value === currentPeriod;
                    return (
                        <button
                            key={month}
                            onClick={() => { onSelectPeriod(value); onClose(); }}
                            className={`p-2 rounded-lg text-sm transition-colors ${isSelected ? 'bg-primary text-white font-bold' : 'hover:bg-gray-200 text-gray-700'}`}
                        >
                            {month}
                        </button>
                    );
                })}
            </div>
        </div>
    );
};

const ImportModal: React.FC<{
    onClose: () => void;
    onDownloadTemplate: () => void;
    onTriggerUpload: () => void;
}> = ({ onClose, onDownloadTemplate, onTriggerUpload }) => (
    <Modal title="Import Dữ liệu Nước" onClose={onClose} size="md">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 p-4">
            <button
                onClick={onDownloadTemplate}
                className="flex flex-col items-center justify-center p-6 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300 hover:bg-gray-100 hover:border-primary transition-colors"
            >
                <DocumentArrowDownIcon className="w-10 h-10 text-primary mb-2" />
                <span className="font-semibold text-gray-800">Tải File Mẫu</span>
            </button>
            <button
                onClick={onTriggerUpload}
                className="flex flex-col items-center justify-center p-6 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300 hover:bg-gray-100 hover:border-primary transition-colors"
            >
                <UploadIcon className="w-10 h-10 text-primary mb-2" />
                <span className="font-semibold text-gray-800">Chọn File từ máy</span>
            </button>
        </div>
    </Modal>
);

const FullListModal: React.FC<{
    title: string;
    data: any[];
    type: 'highest' | 'increase';
    onClose: () => void;
}> = ({ title, data, type, onClose }) => {
    const headers = type === 'highest'
        ? ['Căn hộ', 'Chỉ số cũ', 'Chỉ số mới', 'Tiêu thụ']
        : ['Căn hộ', 'Chỉ số cũ', 'Chỉ số mới', 'Tiêu thụ', 'Tăng trưởng'];

    return (
        <Modal title={title} onClose={onClose} size="2xl">
            <div className="overflow-auto max-h-[70vh] border rounded-lg bg-white text-gray-900">
                <table className="min-w-full text-sm">
                    <thead className="bg-gray-50 sticky top-0">
                        <tr>
                            {headers.map(h => <th key={h} className={`px-4 py-2 text-left font-semibold text-gray-600 ${h !== 'Căn hộ' ? 'text-right' : ''}`}>{h}</th>)}
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                        {data.map((item) => (
                            <tr key={item.unitId}>
                                <td className="px-4 py-2 font-medium text-gray-900">{item.unitId}</td>
                                <td className="px-4 py-2 text-right text-gray-800">{item.prevIndex?.toLocaleString('vi-VN')}</td>
                                <td className="px-4 py-2 text-right text-gray-800">{item.currIndex?.toLocaleString('vi-VN')}</td>
                                <td className="px-4 py-2 text-right font-bold text-gray-900">{item.consumption?.toLocaleString('vi-VN')} m³</td>
                                {type === 'increase' && (
                                    <td className="px-4 py-2 text-right font-bold text-red-500">
                                        {isFinite(item.percentIncrease) ? `+${item.percentIncrease.toFixed(1)}%` : 'Mới có số'}
                                    </td>
                                )}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </Modal>
    );
};
const IndividualHistoryModal: React.FC<{
    unitId: string;
    analytics: any;
    readings: WaterReading[];
    onClose: () => void;
}> = ({ unitId, analytics, readings, onClose }) => {
    if (!analytics) return null;
    
    const chartData = [...readings].reverse().map(r => ({
        name: r.Period.slice(5) + '/' + r.Period.slice(2, 4),
        'Tiêu thụ': r.consumption
    }));

    return (
        <Modal title={`Lịch sử tiêu thụ - ${unitId}`} onClose={onClose} size="lg">
            <div className="p-4 space-y-6">
                <div className="grid grid-cols-3 gap-4">
                    <div className="bg-blue-50 p-3 rounded-lg text-center">
                        <p className="text-xs text-blue-600 font-medium mb-1">Trung bình</p>
                        <p className="text-lg font-bold text-blue-900">{analytics.average.toFixed(1)} <span className="text-xs font-normal">m³</span></p>
                    </div>
                    <div className="bg-red-50 p-3 rounded-lg text-center">
                        <p className="text-xs text-red-600 font-medium mb-1">Cao nhất</p>
                        <p className="text-lg font-bold text-red-900">{analytics.highest?.consumption ?? 0} <span className="text-xs font-normal">m³</span></p>
                        <p className="text-[10px] text-red-500 mt-1">{analytics.highest?.Period}</p>
                    </div>
                    <div className="bg-green-50 p-3 rounded-lg text-center">
                        <p className="text-xs text-green-600 font-medium mb-1">Thấp nhất</p>
                        <p className="text-lg font-bold text-green-900">{analytics.lowest?.consumption ?? 0} <span className="text-xs font-normal">m³</span></p>
                        <p className="text-[10px] text-green-500 mt-1">{analytics.lowest?.Period}</p>
                    </div>
                </div>

                <div className="h-64 bg-gray-50 p-4 rounded-xl border border-gray-100">
                    <p className="text-sm font-bold text-gray-700 mb-4">Biểu đồ tiêu thụ (6 tháng)</p>
                    <ResponsiveContainer width="100%" height="100%">
                        <LineChart data={chartData}>
                            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e5e7eb" />
                            <XAxis dataKey="name" fontSize={11} tickLine={false} axisLine={false} />
                            <YAxis fontSize={11} tickLine={false} axisLine={false} width={30} />
                            <Tooltip 
                                contentStyle={{ borderRadius: '12px', border: 'none', boxShadow: '0 10px 15px -3px rgb(0 0 0 / 0.1)' }}
                                cursor={{ stroke: '#3b82f6', strokeWidth: 2 }}
                            />
                            <Line 
                                type="monotone" 
                                dataKey="Tiêu thụ" 
                                stroke="#3b82f6" 
                                strokeWidth={3} 
                                dot={{ r: 4, fill: '#3b82f6', strokeWidth: 2, stroke: '#fff' }}
                                activeDot={{ r: 6, strokeWidth: 0 }}
                            />
                        </LineChart>
                    </ResponsiveContainer>
                </div>

                <div className="overflow-hidden border rounded-lg">
                    <table className="min-w-full text-sm">
                        <thead className="bg-gray-50">
                            <tr>
                                <th className="px-4 py-2 text-left font-semibold text-gray-600">Kỳ</th>
                                <th className="px-4 py-2 text-right font-semibold text-gray-600">Chỉ số cũ</th>
                                <th className="px-4 py-2 text-right font-semibold text-gray-600">Chỉ số mới</th>
                                <th className="px-4 py-2 text-right font-semibold text-gray-600">Tiêu thụ</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200">
                            {readings.map(r => (
                                <tr key={r.Period} className="hover:bg-gray-50">
                                    <td className="px-4 py-2 font-medium text-gray-900">{r.Period}</td>
                                    <td className="px-4 py-2 text-right text-gray-600">{r.PrevIndex.toLocaleString('vi-VN')}</td>
                                    <td className="px-4 py-2 text-right text-gray-600">{r.CurrIndex.toLocaleString('vi-VN')}</td>
                                    <td className="px-4 py-2 text-right font-bold text-blue-600">{r.consumption.toLocaleString('vi-VN')} m³</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        </Modal>
    );
};
// --- END: Child Components ---

const calculateWaterBill = (consumption: number, unitType: UnitType, tariffs: TariffWater[]): number => {
    if (consumption <= 0 || !tariffs || tariffs.length === 0) return 0;
    const sortedTiers = [...tariffs].sort((a, b) => a.From_m3 - b.From_m3);
    if (unitType === UnitType.KIOS) {
        const businessTariff = sortedTiers.find(t => t.To_m3 === null);
        if (!businessTariff) return 0;
        const net = consumption * businessTariff.UnitPrice;
        const vat = net * (businessTariff.VAT_percent / 100);
        return Math.round(net + vat);
    }
    let totalNet = 0;
    let consumptionRemaining = consumption;
    let previousTierEnd = 0;
    for (const tier of sortedTiers) {
        if (consumptionRemaining <= 0) break;
        const currentTierEnd = tier.To_m3 ?? Infinity;
        const tierCapacity = currentTierEnd - previousTierEnd;
        const usageInTier = Math.min(consumptionRemaining, tierCapacity);
        totalNet += usageInTier * tier.UnitPrice;
        consumptionRemaining -= usageInTier;
        previousTierEnd = currentTierEnd;
    }
    const vatPercent = sortedTiers[0]?.VAT_percent ?? 5;
    const totalVat = totalNet * (vatPercent / 100);
    return Math.round(totalNet + totalVat);
};

interface WaterPageProps {
    waterReadings: WaterReading[];
    setWaterReadings: (updater: React.SetStateAction<WaterReading[]>, logPayload?: any) => void;
    allUnits: Unit[];
    role: Role;
    tariffs: TariffCollection;
    lockedPeriods?: string[];
    refreshData?: (force?: boolean) => void;
    activityLogs?: ActivityLog[];
}

const WaterPage: React.FC<WaterPageProps> = ({ waterReadings: globalReadings, setWaterReadings, allUnits, role, tariffs, lockedPeriods = [], refreshData, activityLogs = [] }) => {
    const { showToast } = useNotification();
    const canEdit = ['Admin', 'Operator', 'Accountant'].includes(role);
    const [period, setPeriod] = useState(new Date().toISOString().slice(0, 7));
    
    const [localReadings, setLocalReadings] = useState<WaterReading[]>([]);
    const [isFetching, setIsFetching] = useState(false);

    useEffect(() => {
        let isMounted = true;
        const loadPeriodData = async () => {
            setIsFetching(true);
            try {
                // Fetch 6 months of history to populate charts and analytics
                const periodsToFetch: string[] = [];
                let currentPeriodDate = new Date(period + '-02');
                for (let i = 0; i < 6; i++) {
                    periodsToFetch.push(currentPeriodDate.toISOString().slice(0, 7));
                    currentPeriodDate.setMonth(currentPeriodDate.getMonth() - 1);
                }
                
                const data = await fetchRecentWaterReadings(periodsToFetch);
                if (isMounted) setLocalReadings(data);
            } catch (error) {
                console.error("Failed to fetch water readings:", error);
                showToast("Lỗi tải dữ liệu nước.", "error");
            } finally {
                if (isMounted) setIsFetching(false);
            }
        };
        loadPeriodData();
        return () => { isMounted = false; };
    }, [period]);

    const [searchTerm, setSearchTerm] = useState('');
    const [floorFilter, setFloorFilter] = useState('all');
    const [kpiFilter, setKpiFilter] = useState<'residential' | 'business' | 'unrecorded' | null>(null);
    const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});
    const inputRefs = useRef<Record<string, HTMLInputElement>>({});
    const [isMonthPickerOpen, setIsMonthPickerOpen] = useState(false);
    const [isLocking, setIsLocking] = useState(false);
    const [isImportModalOpen, setIsImportModalOpen] = useState(false);
    const [isImportManualLocked, setIsImportManualLocked] = useState(false);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [selectedUnitId, setSelectedUnitId] = useState<string | null>(null);
    const [fullListModalData, setFullListModalData] = useState<{title: string; data: any[]; type: 'highest' | 'increase'} | null>(null);
    const [localLockedState, setLocalLockedState] = useState<boolean | null>(null);

    useEffect(() => { setLocalLockedState(null); }, [period]);

    const isLocked = useMemo(() => {
        if (localLockedState !== null) return localLockedState;
        return lockedPeriods.includes(period);
    }, [period, lockedPeriods, localLockedState]);

    const waterReadingsMap = useMemo(() => {
        const map = new Map<string, WaterReading[]>();
        localReadings.forEach(r => { if (!map.has(r.UnitID)) map.set(r.UnitID, []); map.get(r.UnitID)!.push(r); });
        return map;
    }, [localReadings]);

    const waterData = useMemo(() => {
        const prevReadingPeriod = getPreviousPeriod(period);
        const prevPeriodReadings = new Map<string, WaterReading>(localReadings.filter(r => r.Period === prevReadingPeriod).map(r => [r.UnitID, r]));
        const currentPeriodReadings = new Map<string, WaterReading>(localReadings.filter(r => r.Period === period).map(r => [r.UnitID, r]));
        return allUnits.map(unit => {
            const prevReading = prevPeriodReadings.get(unit.UnitID);
            const currentReading = currentPeriodReadings.get(unit.UnitID);
            return { unitId: unit.UnitID, unitType: unit.UnitType, prevIndex: prevReading?.CurrIndex ?? currentReading?.PrevIndex ?? 0, currIndex: currentReading?.CurrIndex ?? null, consumption: currentReading?.consumption ?? null, };
        }).sort((a, b) => sortUnitsComparator({ UnitID: a.unitId }, { UnitID: b.unitId }));
    }, [period, localReadings, allUnits]);

    const historicalChartData = useMemo(() => {
        const data = [];
        let currentPeriodDate = new Date(period + '-02');
        for (let i = 0; i < 6; i++) {
            const p = currentPeriodDate.toISOString().slice(0, 7);
            let consumption = localReadings.filter(r => r.Period === p).reduce((total, reading) => total + (reading.consumption ?? 0), 0);
            data.push({ name: `${String(currentPeriodDate.getMonth() + 1).padStart(2, '0')}/${currentPeriodDate.getFullYear().toString().slice(2)}`, 'Tiêu thụ': consumption });
            currentPeriodDate.setMonth(currentPeriodDate.getMonth() - 1);
        }
        return data.reverse();
    }, [period, localReadings]);

    const analyticsData = useMemo(() => {
        const prevPeriod = getPreviousPeriod(period);
        const prevPeriodConsumptionMap = new Map<string, number>();
        localReadings.filter(r => r.Period === prevPeriod).forEach(r => { prevPeriodConsumptionMap.set(r.UnitID, r.consumption ?? 0); });
        const fullHighestList = waterData.filter(d => d.consumption !== null && d.consumption > 0).sort((a, b) => (b.consumption ?? 0) - (a.consumption ?? 0));
        const fullIncreaseList = waterData.map(d => {
            const prevConsumption = prevPeriodConsumptionMap.get(d.unitId);
            if (prevConsumption !== undefined && d.consumption! > prevConsumption) {
                const increase = d.consumption! - prevConsumption;
                const percentIncrease = prevConsumption > 0 ? (increase / prevConsumption) * 100 : Infinity;
                return { ...d, increase, percentIncrease };
            }
            return null;
        }).filter((item): item is NonNullable<typeof item> => item !== null).sort((a, b) => b.percentIncrease - a.percentIncrease);
        return { top5Highest: fullHighestList.slice(0, 5), top5Increases: fullIncreaseList.slice(0, 5).map(item => ({ ...item, increase: item.increase, current: item.consumption, previous: item.consumption! - item.increase })), fullHighestList, fullIncreaseList };
    }, [waterData, period, localReadings]);

    const individualUnitAnalytics = useMemo(() => {
        if (!selectedUnitId) return null;
        const uniqueReadings = localReadings.filter(r => r.UnitID === selectedUnitId && r.consumption > 0).sort((a, b) => b.Period.localeCompare(a.Period)).slice(0, 6);
        if (uniqueReadings.length === 0) return { highest: null, lowest: null, average: 0, readings: [] };
        const highest = uniqueReadings.reduce((max, current) => current.consumption > max.consumption ? current : max, uniqueReadings[0]);
        const lowest = uniqueReadings.reduce((min, current) => current.consumption < min.consumption ? current : min, uniqueReadings[0]);
        const total = uniqueReadings.reduce((sum, current) => sum + current.consumption, 0);
        const average = total / uniqueReadings.length;
        return { highest, lowest, average, readings: uniqueReadings };
    }, [selectedUnitId, localReadings]);
    
    const waterLogs = useMemo(() => {
        return activityLogs.filter(log => log.module === 'Water' || log.summary.toLowerCase().includes('nước')).slice(0, 10);
    }, [activityLogs]);

    const stats = useMemo(() => {
        const recorded = waterData.filter(d => d.currIndex !== null).length;
        const totalUnits = waterData.length;
        const totalConsumption = waterData.reduce((sum, d) => sum + (d.consumption || 0), 0);
        const avgConsumption = recorded > 0 ? totalConsumption / recorded : 0;
        
        return { recorded, totalUnits, totalConsumption, avgConsumption };
    }, [waterData]);

    // Auto-lock import when all units are recorded
    useEffect(() => {
        if (stats.recorded > 0 && stats.recorded === stats.totalUnits) {
            setIsImportManualLocked(true);
        }
    }, [stats.recorded, stats.totalUnits]);
    
    const totalWaterBill = useMemo(() => Math.round(waterData.reduce((total, unitData) => (unitData.consumption === null || unitData.consumption < 0) ? total : total + calculateWaterBill(unitData.consumption, unitData.unitType, tariffs.water), 0)), [waterData, tariffs.water]);
    
    const filteredWaterData = useMemo(() => {
        return waterData.filter(d => {
            if (kpiFilter) {
                if (kpiFilter === 'residential' && d.unitType !== UnitType.APARTMENT) return false;
                if (kpiFilter === 'business' && d.unitType !== UnitType.KIOS) return false;
                if (kpiFilter === 'unrecorded' && d.currIndex !== null) return false;
            }
            if (floorFilter !== 'all') {
                const floor = parseUnitCode(d.unitId)?.floor;
                const unitFloor = d.unitType === UnitType.KIOS ? 'KIOS' : String(floor);
                if (unitFloor !== floorFilter) return false;
            }
            if (searchTerm && !d.unitId.toLowerCase().includes(searchTerm.toLowerCase())) return false;
            return true;
        });
    }, [waterData, searchTerm, floorFilter, kpiFilter]);

    const floors = useMemo(() => {
        const floorNumbers = Array.from(new Set(allUnits.filter(u => u.UnitType === UnitType.APARTMENT).map(u => u.UnitID.slice(0, -2)))).sort((a, b) => parseInt(String(a), 10) - parseInt(String(b), 10));
        return [{ value: 'all', label: 'Tất cả các tầng' }, ...floorNumbers.map(f => ({ value: f, label: `Tầng ${f}` })), { value: 'KIOS', label: 'Kios' }];
    }, [allUnits]);

    const handleSave = async (unitId: string, newIndexStr: string) => {
        const newIndex = parseInt(newIndexStr, 10);
        const errors = { ...validationErrors };
        if (newIndexStr === '' || isNaN(newIndex) || newIndex < 0) {
            delete errors[unitId];
            setValidationErrors(errors);
            setWaterReadings(prev => prev.filter(r => !(r.UnitID === unitId && r.Period === period)), { module: 'Water', action: 'DELETE_WATER_READING', summary: `Xóa số nước cho ${unitId} kỳ ${period}`, ids: [unitId], });
            showToast(`Đã xóa chỉ số cho căn hộ ${unitId}.`, 'info');
            return;
        }
        const prevReadingPeriod = getPreviousPeriod(period);
        const prevReading = localReadings.find(r => r.Period === prevReadingPeriod && r.UnitID === unitId);
        const prevIndex = prevReading?.CurrIndex ?? 0;
        if (newIndex < prevIndex) {
            errors[unitId] = "Chỉ số mới phải lớn hơn hoặc bằng chỉ số cũ.";
            setValidationErrors(errors);
            return;
        }
        delete errors[unitId];
        setValidationErrors(errors);
        const consumption = newIndex - prevIndex;
        const newReading: WaterReading = { UnitID: unitId, Period: period, PrevIndex: prevIndex, CurrIndex: newIndex, Rollover: false, consumption: Math.max(0, consumption) };
        
        try {
            await saveWaterReadings([newReading]);
            setLocalReadings(prev => [...prev.filter(r => !(r.UnitID === unitId && r.Period === period)), newReading]);
            if (refreshData) refreshData(true);
            showToast(`Đã lưu chỉ số mới cho căn hộ ${unitId}.`, 'success');
        } catch (error) {
            showToast('Lỗi khi lưu dữ liệu.', 'error');
        }
    };

    const handleResetAll = async () => {
        if (isLocked || !canEdit) return;
        if (!window.confirm(`Bạn có chắc chắn muốn XÓA TẤT CẢ chỉ số đã nhập trong kỳ ${period}? Hành động này không thể hoàn tác.`)) return;
        
        setIsFetching(true);
        try {
            const currentReadings = localReadings.filter(r => r.Period === period);
            const resetReadings = currentReadings.map(r => ({ ...r, CurrIndex: r.PrevIndex, consumption: 0 }));
            // To properly reset, we should delete them from DB or save them as empty/same as prev
            // Based on handleSave logic, sending empty string/null deletes them.
            // Let's delete them for this period.
            const readingsToKeep = localReadings.filter(r => r.Period !== period);
            await saveWaterReadings(readingsToKeep); // Re-save without this period
            setLocalReadings(readingsToKeep);
            if (refreshData) refreshData(true);
            showToast(`Đã xóa sạch chỉ số nước kỳ ${period}.`, 'success');
        } catch (error) {
            showToast('Lỗi khi reset dữ liệu.', 'error');
        } finally {
            setIsFetching(false);
        }
    };

    const handleManualRefresh = async () => {
        setIsFetching(true);
        try {
            const prev = getPreviousPeriod(period);
            const data = await fetchRecentWaterReadings([period, prev]);
            setLocalReadings(data);
            showToast("Đã cập nhật dữ liệu mới nhất.", "success");
        } catch(e) { showToast("Lỗi khi tải dữ liệu.", "error"); } 
        finally { setIsFetching(false); }
    };

    const handleDownloadTemplate = () => {
        const templateData = allUnits.map(u => ({
            'Căn hộ': u.UnitID,
            'Chỉ số cũ': waterData.find(d => d.unitId === u.UnitID)?.prevIndex || 0,
            'Chỉ số mới': ''
        }));
        const ws = XLSX.utils.json_to_sheet(templateData);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "WaterImport");
        XLSX.writeFile(wb, `Mau_Import_Nuoc_${period}.xlsx`);
    };

    const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;
        setIsFetching(true);
        try {
            const parsed = await processImportFile(file, {
                unitId: ['căn hộ', 'mã căn', 'unit'],
                reading: ['chỉ số mới', 'mới', 'reading', 'index']
            });

            const newReadings: WaterReading[] = [];
            parsed.forEach(row => {
                const unitId = String(row.unitId);
                const readingValue = parseInt(String(row.reading), 10);
                if (!unitId || isNaN(readingValue)) return;

                const unitData = waterData.find(d => d.unitId === unitId);
                if (!unitData) return;

                const prevIndex = unitData.prevIndex || 0;
                if (readingValue < prevIndex) return;

                newReadings.push({
                    UnitID: unitId,
                    Period: period,
                    PrevIndex: prevIndex,
                    CurrIndex: readingValue,
                    Rollover: false,
                    consumption: readingValue - prevIndex
                });
            });

            if (newReadings.length > 0) {
                await saveWaterReadings(newReadings);
                setLocalReadings(prev => {
                    const filtered = prev.filter(r => !newReadings.some(nr => nr.UnitID === r.UnitID && nr.Period === r.Period));
                    return [...filtered, ...newReadings];
                });
                showToast(`Đã import thành công ${newReadings.length} bản ghi.`, 'success');
                setIsImportModalOpen(false);
            } else {
                showToast('Không tìm thấy dữ liệu hợp lệ để import.', 'warn');
            }
        } catch (error) {
            showToast('Lỗi khi xử lý file import.', 'error');
        } finally {
            setIsFetching(false);
            if (fileInputRef.current) fileInputRef.current.value = '';
        }
    };

    return (
        <div className="flex gap-6 h-full overflow-hidden">
            {fullListModalData && <FullListModal {...fullListModalData} onClose={() => setFullListModalData(null)} />}
            {isImportModalOpen && (
                <ImportModal 
                    onClose={() => setIsImportModalOpen(false)} 
                    onDownloadTemplate={handleDownloadTemplate} 
                    onTriggerUpload={() => fileInputRef.current?.click()} 
                />
            )}
            <input type="file" ref={fileInputRef} className="hidden" onChange={handleFileUpload} accept=".xlsx, .xls, .csv" />
            {selectedUnitId && <IndividualHistoryModal unitId={selectedUnitId} analytics={individualUnitAnalytics} readings={individualUnitAnalytics?.readings || []} onClose={() => setSelectedUnitId(null)} />}

            <div className="flex-1 flex flex-col gap-4 min-w-0">
                {/* Statistics Cards */}
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4 flex-shrink-0">
                    <StatCard 
                        label="Tổng tiêu thụ" 
                        value={`${stats.totalConsumption.toLocaleString('vi-VN')} m³`} 
                        icon={<DropletsIcon className="w-7 h-7 text-blue-600" />} 
                        iconBgClass="bg-blue-50"
                        trend={historicalChartData.length >= 2 ? ((historicalChartData[historicalChartData.length-1]['Tiêu thụ'] - historicalChartData[historicalChartData.length-2]['Tiêu thụ']) / (historicalChartData[historicalChartData.length-2]['Tiêu thụ'] || 1)) * 100 : undefined}
                    />
                    <StatCard 
                        label="Doanh thu ước tính" 
                        value={formatCurrency(totalWaterBill)} 
                        icon={<TrendingUpIcon className="w-7 h-7 text-emerald-600" />} 
                        iconBgClass="bg-emerald-50"
                    />
                    <StatCard 
                        label="Đã ghi chỉ số" 
                        value={`${stats.recorded}/${stats.totalUnits}`} 
                        icon={<SparklesIcon className="w-7 h-7 text-amber-600" />} 
                        iconBgClass="bg-amber-50"
                        subtext={`${((stats.recorded / stats.totalUnits) * 100).toFixed(1)}% hoàn thành`}
                    />
                    <StatCard 
                        label="Tiêu thụ trung bình" 
                        value={`${stats.avgConsumption.toFixed(1)} m³`} 
                        icon={<HomeIcon className="w-7 h-7 text-purple-600" />} 
                        iconBgClass="bg-purple-50"
                    />
                </div>

                <div className="bg-white p-4 rounded-xl shadow-sm flex-shrink-0">
                     <div className="flex flex-col md:flex-row md:items-center gap-4">
                         <div className="flex items-center gap-2">
                             <div className="relative flex items-center gap-1 p-1 bg-gray-100 rounded-lg">
                                 <button onClick={() => setPeriod(getPreviousPeriod(period))} disabled={isFetching} className="p-1 rounded hover:bg-white transition-colors"><ChevronLeftIcon /></button>
                                 <button onClick={() => setIsMonthPickerOpen(p => !p)} className="p-1.5 w-32 font-semibold hover:bg-white rounded-md transition-colors">{isFetching ? <Spinner className="w-4 h-4 mx-auto"/> : new Date(period + '-02').toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}</button>
                                 {isMonthPickerOpen && <MonthPickerPopover currentPeriod={period} onSelectPeriod={setPeriod} onClose={() => setIsMonthPickerOpen(false)}/>}
                                 <button onClick={() => setPeriod(p => { const d=new Date(p+'-02'); d.setMonth(d.getMonth()+1); return d.toISOString().slice(0,7);})} disabled={isFetching} className="p-1 rounded hover:bg-white transition-colors"><ChevronRightIcon /></button>
                             </div>
                         </div>

                         <div className="relative flex-grow"><SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" /><input type="text" placeholder="Tìm căn hộ..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="w-full h-10 pl-10 pr-3 border rounded-lg bg-gray-50 border-gray-200 focus:bg-white focus:ring-2 focus:ring-primary"/></div>
                         <select value={floorFilter} onChange={e => setFloorFilter(e.target.value)} className="h-10 px-3 border rounded-lg bg-gray-50 border-gray-200 focus:ring-2 focus:ring-primary">{floors.map(f => <option key={f.value} value={f.value}>{f.label}</option>)}</select>
                         <div className="flex items-center gap-2 border-l pl-4">
                            <button onClick={handleManualRefresh} className="h-10 px-3 rounded-lg bg-gray-100 hover:bg-gray-200 border border-transparent" title="Làm mới dữ liệu"><ArrowPathIcon className={`w-5 h-5 ${isFetching ? 'animate-spin' : ''}`} /></button>
                            {canEdit && !isLocked && (
                                <button onClick={handleResetAll} className="h-10 px-4 font-semibold rounded-lg flex items-center gap-2 border border-red-200 text-red-600 hover:bg-red-50 bg-white" title="Xóa toàn bộ chỉ số kỳ này"><TrashIcon className="w-5 h-5" /> Reset</button>
                            )}
                            {isLocked ? (
                                <button className="h-10 px-4 font-semibold rounded-lg flex items-center gap-2 bg-gray-400 text-white cursor-not-allowed"><LockClosedIcon className="w-5 h-5" /> Đã chốt</button>
                            ) : (
                                <button onClick={() => {}} className="h-10 px-4 font-semibold rounded-lg flex items-center gap-2 bg-primary text-white hover:bg-primary-focus"><SaveIcon className="w-5 h-5" /> Chốt sổ</button>
                            )}
                            <button 
                                onClick={() => !isImportManualLocked && setIsImportModalOpen(true)} 
                                onDoubleClick={() => setIsImportManualLocked(!isImportManualLocked)}
                                disabled={!canEdit || isLocked} 
                                className={`h-10 px-4 font-semibold rounded-lg flex items-center gap-2 border transition-all select-none ${
                                    isImportManualLocked 
                                    ? 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed' 
                                    : 'border-primary text-primary hover:bg-primary/10 bg-white'
                                } disabled:opacity-50`}
                                title={isImportManualLocked ? "Bấm đúp để mở khóa Import" : "Bấm đúp để khóa nút Import"}
                            >
                                {isImportManualLocked ? <LockClosedIcon className="w-4 h-4" /> : <UploadIcon />} 
                                Import
                            </button>
                        </div>
                    </div>
                </div>

                <div className="bg-white rounded-xl shadow-sm flex-1 flex flex-col overflow-hidden">
                    <div className="overflow-y-auto pr-2">
                        <table className="min-w-full">
                            <thead className="bg-gray-50 sticky top-0 z-10">
                                <tr>
                                    <th className="px-4 py-3 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">Căn hộ</th>
                                    <th className="px-4 py-3 text-right text-xs font-bold text-gray-700 uppercase tracking-wider">Chỉ số cũ</th>
                                    <th className="px-4 py-3 text-right text-xs font-bold text-gray-700 uppercase tracking-wider">Chỉ số mới</th>
                                    <th className="px-4 py-3 text-right text-xs font-bold text-gray-700 uppercase tracking-wider">Tiêu thụ (m³)</th>
                                    <th className="px-4 py-3 text-center text-xs font-bold text-gray-700 uppercase tracking-wider">Lịch sử</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100">
                                {filteredWaterData.map(d => (
                                    <tr key={d.unitId} className={`transition-colors text-sm ${selectedUnitId === d.unitId ? 'bg-blue-50' : 'hover:bg-gray-50'}`}>
                                        <td className="font-semibold px-4 py-3 text-gray-900">{d.unitId}</td>
                                        <td className="px-4 py-3 text-right text-gray-500">{d.prevIndex?.toLocaleString('vi-VN') ?? 'N/A'}</td>
                                        <td className="px-4 py-3 text-right">
                                            <input 
                                                ref={el => { if (el) inputRefs.current[d.unitId] = el; }} 
                                                type="number" 
                                                defaultValue={d.currIndex ?? ''} 
                                                key={`${d.unitId}-${d.currIndex}`} 
                                                onBlur={e => handleSave(d.unitId, e.target.value)} 
                                                onKeyDown={e => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); }} 
                                                disabled={!canEdit || isLocked || isFetching} 
                                                className={`w-32 text-right p-2 border rounded-md bg-gray-50 focus:bg-white text-gray-900 focus:ring-2 focus:ring-primary ${validationErrors[d.unitId] ? 'border-red-500' : 'border-gray-300'} disabled:bg-transparent disabled:border-transparent`}
                                            />
                                            {validationErrors[d.unitId] && <p className="text-red-500 text-xs text-right mt-1">{validationErrors[d.unitId]}</p>}
                                        </td>
                                        <td className={`font-bold px-4 py-3 text-right ${d.consumption && d.consumption > 30 ? 'text-red-600' : (d.consumption && d.consumption > 20 ? 'text-yellow-600' : 'text-green-600')}`}>
                                            {d.consumption !== null ? `${d.consumption.toLocaleString('vi-VN')} m³` : 'Chưa có'}
                                        </td>
                                        <td className="px-4 py-3 text-center">
                                            <button onClick={() => setSelectedUnitId(d.unitId)} className="p-2 rounded-full hover:bg-gray-200">
                                                <EyeIcon className="w-5 h-5 text-blue-600"/>
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>

            {/* Right Sidebar - Analytics & History */}
            <div className="w-80 flex-shrink-0 flex flex-col gap-4 overflow-y-auto pr-2">
                {/* Overall Stats */}
                <div className="bg-white p-4 rounded-xl shadow-sm">
                    <h3 className="text-lg font-bold text-gray-800 mb-4 flex items-center gap-2">
                        <TrendingUpIcon className="w-5 h-5 text-primary" />
                        Thống kê kỳ {period}
                    </h3>
                    <div className="space-y-4">
                        <div>
                            <p className="text-sm text-gray-500 mb-1">Tổng tiêu thụ</p>
                            <p className="text-2xl font-bold text-gray-900">
                                {waterData.reduce((sum, d) => sum + (d.consumption || 0), 0).toLocaleString('vi-VN')} <span className="text-sm font-normal text-gray-500">m³</span>
                            </p>
                        </div>
                        <div>
                            <p className="text-sm text-gray-500 mb-1">Ước tính doanh thu</p>
                            <p className="text-xl font-bold text-primary">
                                {formatCurrency(totalWaterBill)}
                            </p>
                        </div>
                        
                        {/* Chart */}
                        <div className="h-48 mt-4">
                            <ResponsiveContainer width="100%" height="100%">
                                <BarChart data={historicalChartData}>
                                    <CartesianGrid strokeDasharray="3 3" vertical={false} />
                                    <XAxis dataKey="name" fontSize={12} tickLine={false} axisLine={false} />
                                    <YAxis fontSize={12} tickLine={false} axisLine={false} width={40} />
                                    <Tooltip cursor={{fill: '#f3f4f6'}} contentStyle={{borderRadius: '8px', border: 'none', boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)'}} />
                                    <Bar dataKey="Tiêu thụ" fill="#0ea5e9" radius={[4, 4, 0, 0]} />
                                </BarChart>
                            </ResponsiveContainer>
                        </div>
                    </div>
                </div>

                {/* Top Consumers */}
                <div className="bg-white p-4 rounded-xl shadow-sm">
                    <div className="flex items-center justify-between mb-4">
                        <h3 className="text-sm font-bold text-gray-800">Tiêu thụ cao nhất</h3>
                        <button onClick={() => setFullListModalData({ title: 'Top tiêu thụ cao nhất', data: analyticsData.fullHighestList, type: 'highest' })} className="text-xs text-primary hover:underline">Xem tất cả</button>
                    </div>
                    <div className="space-y-3">
                        {analyticsData.top5Highest.map((item, idx) => (
                            <div key={item.unitId} className="flex items-center justify-between">
                                <div className="flex items-center gap-2">
                                    <span className="text-xs font-bold text-gray-400 w-4">{idx + 1}.</span>
                                    <span className="text-sm font-medium text-gray-700">{item.unitId}</span>
                                </div>
                                <span className="text-sm font-bold text-gray-900">{item.consumption?.toLocaleString('vi-VN')} m³</span>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Top Increases */}
                <div className="bg-white p-4 rounded-xl shadow-sm">
                    <div className="flex items-center justify-between mb-4">
                        <h3 className="text-sm font-bold text-gray-800">Tăng đột biến</h3>
                        <button onClick={() => setFullListModalData({ title: 'Top tăng đột biến', data: analyticsData.fullIncreaseList, type: 'increase' })} className="text-xs text-primary hover:underline">Xem tất cả</button>
                    </div>
                    <div className="space-y-3">
                        {analyticsData.top5Increases.map((item, idx) => (
                            <div key={item.unitId} className="flex items-center justify-between">
                                <div className="flex items-center gap-2">
                                    <span className="text-xs font-bold text-gray-400 w-4">{idx + 1}.</span>
                                    <span className="text-sm font-medium text-gray-700">{item.unitId}</span>
                                </div>
                                <div className="text-right">
                                    <span className="text-sm font-bold text-red-500">+{item.percentIncrease.toFixed(0)}%</span>
                                    <p className="text-xs text-gray-500">{item.previous} → {item.current} m³</p>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Activity History */}
                <div className="bg-white p-4 rounded-xl shadow-sm flex-1 min-h-0 flex flex-col">
                    <h3 className="text-sm font-bold text-gray-800 mb-4 flex items-center gap-2">
                        <ClockIcon className="w-4 h-4 text-gray-500" />
                        Lịch sử hoạt động
                    </h3>
                    <div className="flex-1 overflow-y-auto space-y-4 pr-1">
                        {waterLogs.length > 0 ? (
                            waterLogs.map((log) => (
                                <div key={log.id} className="flex gap-3 text-xs">
                                    <div className="flex-shrink-0 mt-0.5">
                                        <UserCircleIcon className="w-6 h-6 text-gray-400" />
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <p className="text-gray-900 font-medium truncate">{log.actor_email.split('@')[0]}</p>
                                        <p className="text-gray-600 line-clamp-2">{log.summary}</p>
                                        <p className="text-gray-400 mt-1">{timeAgo(log.ts)}</p>
                                    </div>
                                </div>
                            ))
                        ) : (
                            <div className="text-center py-8 text-gray-400 text-xs italic">
                                Chưa có hoạt động nào
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default WaterPage;
